import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaqTemp from '@/features/Listings/seo/search-results-content/content/average-rent-faq-temp'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'

const Winnipeg = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const getAbout = () => {
    return (
      <Trans i18nKey="seo.city.winnipeg:About Winnipeg Paragraph">
        <Paragraph>
          Winnipeg, the capital of{' '}
          <LinkText href="/rental-listings/manitoba">Manitoba</LinkText>, is a
          lively city known for its rich cultural heritage, distinct seasons,
          and strong community spirit. Located at the confluence of the Red and
          Assiniboine Rivers, Winnipeg is famous for attractions like The Forks,
          a historic meeting place, and the Canadian Museum for Human Rights.
          The city hosts numerous festivals, including the Winnipeg Folk
          Festival and Folklorama, celebrating its diversity. While institutions
          like the{' '}
          <LinkText href="/rental-listings/city/winnipeg/university-of-winnipeg">
            University of Winnipeg
          </LinkText>{' '}
          and the{' '}
          <LinkText href="/rental-listings/city/winnipeg/university-of-manitoba">
            University of Manitoba
          </LinkText>{' '}
          contribute to the educational landscape, Winnipeg is equally cherished
          for its local arts, music, and sports, including passionate support
          for the Winnipeg Jets.
        </Paragraph>
      </Trans>
    )
  }

  const getRentingIn = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.winnipeg:Renting FILTER in CITY Paragraph">
            Finding apartments for rent in Winnipeg involves research,
            communication with{' '}
            <LinkText href="/landlords">landlords & property managers</LinkText>
            , securing references, signing a lease, depositing rent, and
            learning about all the applicable{' '}
            <LinkText href="https://liv.rent/blog/landlords/rental-dictionary-essential-terms-for-renters-and-landlords/">
              rental laws & terminology
            </LinkText>
            . All this can add up to a lengthy endeavour if you’re not familiar
            with how to find an apartment for rent in Winnipeg. To help you get
            started, here is a list of resources with tips on renting in Canada.
          </Trans>
        </Paragraph>
        <LinkList>
          <Trans i18nKey="seo:Renting FILTER near LOCATION in Canada Links">
            <LinkItem href="https://liv.rent/blog/renters/how-does-rent-work-in-canada-a-provincial-comparison/">
              How does rent work in Canada (compare by province)
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/">
              What $1800 in rent can get you across Canada
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/renters/rent-in-canada-how-to-secure-a-home-before-coming-to-canada/">
              How to secure a home before arriving in Canada
            </LinkItem>
          </Trans>
        </LinkList>
      </>
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaqTemp
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,000 - $1,700',
          bed2: '$1,400 - $2,000',
          bed3: '$1,850 - $2,400'
        }}
        rentReportCity={t('cities:city_name_halifax')}
        rentReportLink="https://liv.rent/blog/rent-reports"
        rentReportLinkZh="https://liv.rent/blog/zh/%e7%a7%9f%e9%87%91%e7%bb%9f%e8%ae%a1/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in CITY Paragraph">
            Finding affordable rentals in {{ cityName }} is easier compared to
            other major cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and{' '}
            <LinkText href="/rental-listings/city/toronto">Toronto</LinkText>,
            especially if you’re looking for{' '}
            <LinkText href={links.under1000}>rentals under $1000</LinkText>.
            Along with effective negotiation skills, you may be able to lower
            the rent price further. For helpful tips on negotiating rent, refer
            to our guide on{' '}
            <LinkText href="https://liv.rent/blog/renters/how-to-negotiate-rent/">
              how to negotiate rent.
            </LinkText>
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in CITY Paragraph 2">
            {{ cityName }} offers a diverse range of neighbourhoods and
            municipalities to choose from when searching for rentals. Depending
            on your preferences, you can explore various rental types, such as{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>, and{' '}
            <LinkText href={links.studio}>studio apartments.</LinkText> Whether
            you’re seeking <LinkText href={links.furnished}>furnished</LinkText>{' '}
            or <LinkText href={links.unfurnished}>unfurnished rentals</LinkText>
            , or even considering{' '}
            <LinkText href={links.longTerm}>long term</LinkText> or{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>{' '}
            options, {{ cityName }} has a variety of choices to suit your needs.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans
              i18nKey="seo:Apartments for rent CITY Paragraph"
              values={{
                location: cityName
              }}
            >
              <LinkText href={links.apartment}>
                Apartments for rent in {{ cityName }}
              </LinkText>{' '}
              are a prevalent housing option, particularly in the urban core,
              appealing to a diverse demographic including students and young
              professionals due to proximity to universities and business areas.
              These apartments often come with modern amenities like fitness
              centers and laundry facilities, enhancing their appeal for those
              seeking convenience. The market offers a range of apartment styles
              and sizes, from studios to three-bedrooms, catering to various
              needs and family sizes.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Houses for rent CITY Paragraph">
              Detached{' '}
              <LinkText href={links.house}>
                houses for rent in {{ cityName }}
              </LinkText>{' '}
              offer a sought-after option for families and individuals in search
              of more space and the privacy of a personal yard. The city’s
              rental market features a diverse selection of properties,
              including both partial houses, such as basements and duplexes, and
              entire houses. Renters can choose from a variety of older,
              character-filled homes and modern constructions, each located in
              different neighbourhoods to suit a range of preferences and needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Townhouses for rent CITY Paragraph 2">
              Townhouses are also common in {{ cityName }}. They combine the
              best qualities of both apartments and whole houses. Typically less
              expensive than houses,{' '}
              <LinkText href={links.townhouse}>
                townhouses for rent in {{ cityName }}
              </LinkText>{' '}
              offer an attractive combination of freedom and space, generally
              coming with your own private yard and entrance.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Basements for rent CITY Paragraph 3">
              A popular alternative for{' '}
              <LinkText href="https://liv.rent/blog/livrent/top-tips-for-first-time-renters/">
                first-time renters
              </LinkText>{' '}
              and anyone else seeking relatively inexpensive rental housing are{' '}
              basements for rent in {{ cityName }}. Since rent prices for
              apartments and houses can often get too expensive, many renters
              seek out basements for rent as a cheaper alternative. Homeowners
              often{' '}
              <LinkText href="https://liv.rent/blog/landlords/how-to-rent-out-your-basement-suite/">
                rent out their basements{' '}
              </LinkText>{' '}
              as a way to offset their mortgage payments. This makes it easy for
              renters to find relatively inexpensive basements for rent in all
              areas of the city.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Condos for rent CITY Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in {{ cityName }}
              </LinkText>{' '}
              are a popular rental option, especially in the downtown area and
              central neighbourhoods where residents value proximity to urban
              amenities and workplaces. These units are ideal for individuals
              and small families who prefer the conveniences of condo living,
              such as built-in maintenance and security services, often
              accompanied by amenities like gyms and pools.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Rooms for rent CITY Paragraph">
              Sharing a house or apartment is practically essential for those
              looking for cheap rent.{' '}
              <LinkText href={links.room}>
                Rooms for rent in {{ cityName }}
              </LinkText>{' '}
              are a great way to experience the best of the city on a budget,
              and can offer you the opportunity to live in neighbourhoods that
              would otherwise be unaffordable.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph 2">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available throughout the city, as well as properties
              where both are allowed. Keep in mind that some {{ cityName }}{' '}
              neighbourhoods are better suited to those with pets, and adjust
              your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph 2">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>
                furnished rentals in {{ cityName }}
              </LinkText>
              . Renters seeking furnished units should search for these units
              using filters, but do be prepared to pay more in rent. There are{' '}
              <LinkText
                href={
                  isChinese
                    ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e7%a7%9f%e5%ae%a2%e9%a1%bb%e7%9f%a5%ef%bc%9a%e5%87%ba%e7%a7%9f%e5%8d%95%e4%bd%8d%e5%b8%a6%e5%ae%b6%e5%85%b7%e8%bf%98%e6%98%af%e4%b8%8d%e5%b8%a6%e5%ae%b6%e5%85%b7-%e7%a7%9f%e9%87%91%e5%8c%ba%e5%88%ab/'
                    : 'https://liv.rent/blog/renters/renting-furnished-unfurnished/'
                }
              >
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.winnipeg:Short term rentals CITY Paragraph">
              Winnipeg, a vibrant city in Manitoba, is not Canada’s largest city
              but is known for its diverse job opportunities and extensive
              educational sector, including several universities and colleges.
              It offers a community-oriented lifestyle with a more relaxed pace
              compared to Canada’s larger metropolitan areas.{' '}
              <LinkText href={links.shortTerm}>Short-term rentals</LinkText> are
              fairly common, driven by the city’s steady influx of students and
              seasonal workers, making it an attractive option for those seeking
              flexibility. This is a key factor when{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                deciding whether or not short-term rentals are suitable.
              </LinkText>{' '}
              For those looking for a quieter experience close to urban
              amenities or temporary lodging during work or study in the region,{' '}
              {{ cityName }}’s rental options are well-suited to meet a variety
              of needs.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Cheap rentals CITY Paragraph">
              Compared to other Canadian cities, rent prices in {{ cityName }}{' '}
              are relatively cheaper. As a result, it shouldn’t be too hard to
              find something within your budget. If you’re looking for{' '}
              <LinkText href={links.cheap}>
                cheap rentals in {{ cityNameWithState }}
              </LinkText>
              , try finding a roommate or two and exploring shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that there are neighbourhoods in {{ cityName }}{' '}
              where rent is considerably cheaper, so exploring these affordable
              options is a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Trans i18nKey="seo.city.winnipeg:Living in Winnipeg Paragraph">
          <Paragraph>
            Living in Winnipeg, Manitoba, offers a unique blend of culture,
            community, and affordability. Known as the “Gateway to the West”,
            Winnipeg is rich in arts, history, and natural beauty, with
            landmarks like The Forks and the Canadian Museum for Human Rights.
            The city has a welcoming vibe, with a diverse population and
            year-round events like the Winnipeg Folk Festival and Festival du
            Voyageur. Winters are cold but bring activities like skating on the
            frozen Red River and sledding, while summers are warm and lively.
            With affordable housing and plenty of green spaces, Winnipeg is an
            appealing and vibrant city to call home.
          </Paragraph>
          <Paragraph>
            If you’re not sure that Winnipeg is the right city for you, check
            out some of these resources comparing the different cities across
            Canada.
          </Paragraph>
        </Trans>
        <Trans i18nKey="seo.city.winnipeg:Living in Winnipeg List">
          <LinkList>
            <LinkItem
              href={
                isChinese
                  ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%95%99%e5%ad%a65%e5%a4%a7%e5%9f%8e%e5%b8%82-%e7%a7%9f%e6%88%bf%e3%80%81%e5%ad%a6%e8%b4%b9%e3%80%81%e5%b7%a5%e4%bd%9c%e3%80%81%e7%ad%be%e8%af%81%e8%af%a6%e8%a7%a3/'
                  : 'https://liv.rent/blog/renters/best-canadian-cities-for-immigration/'
              }
            >
              Best Canadian cities for immigration
            </LinkItem>
            <LinkItem
              href={
                isChinese
                  ? 'https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e6%b8%a9%e5%93%a5%e5%8d%8e%e3%80%81%e5%a4%9a%e4%bc%a6%e5%a4%9a%e6%af%94%e8%be%83/'
                  : 'https://liv.rent/blog/renters/vancouver-or-toronto-a-cost-of-living-comparison/'
              }
            >
              Vancouver vs Toronto: Cost of living comparison
            </LinkItem>
            <LinkItem
              href={
                isChinese
                  ? 'https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E6%B8%A9%E5%93%A5%E5%8D%8E%E5%92%8C%E7%B4%A0%E9%87%8C%E5%B1%85%E4%BD%8F%E6%88%90%E6%9C%AC%E6%AF%94%E8%BE%83/'
                  : 'https://liv.rent/blog/renters/surrey-vs-vancouver-bc-comparison/'
              }
            >
              Vancouver vs Surrey: Cost of living comparison
            </LinkItem>
            <LinkItem
              href={
                isChinese
                  ? 'https://liv.rent/blog/zh/%e6%88%bf%e4%b8%9c/2021%e7%89%88%e5%a4%9a%e4%bc%a6%e5%a4%9a%e3%80%81%e8%92%99%e7%89%b9%e5%88%a9%e5%b0%94%e7%94%9f%e6%b4%bb%e6%88%90%e6%9c%ac%e6%af%94%e8%be%83/'
                  : 'https://liv.rent/blog/renters/toronto-vs-montreal-cost-of-living-comparison/'
              }
            >
              Toronto vs Montreal: Cost of living comparison
            </LinkItem>
          </LinkList>
        </Trans>
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.winnipeg:Popular Neighbourhoods Winnipeg Paragraph">
          Like any city, Winnipeg is made up of smaller neighbourhoods with
          their own distinct features. Some of the most popular neighbourhoods
          include Osborne Village, Wolseley, St. James, Crescentwood, and
          Tuxedo. For more information, you can check out the blog to learn
          about the different{' '}
          <LinkText href="https://liv.rent/blog/neighbourhoods/">
            neighbourhoods
          </LinkText>{' '}
          in Winnipeg.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.winnipeg:Local Landmarks Winnipeg Paragraph">
          Winnipeg, Manitoba, is rich in iconic landmarks that highlight its
          cultural and historical significance.{' '}
          <LinkText href="https://www.theforks.com/">The Forks</LinkText> is a
          central gathering spot with a vibrant market, scenic riverwalk, and
          historical displays, celebrating over 6,000 years of Indigenous and
          settler history. The{' '}
          <LinkText href="https://humanrights.ca/">
            Canadian Museum for Human Rights
          </LinkText>{' '}
          stands as an architectural marvel, dedicated to human rights awareness
          and education.{' '}
          <LinkText href="https://www.assiniboinepark.ca/">
            Assiniboine Park and Zoo
          </LinkText>{' '}
          offer beautiful gardens, walking trails, and a diverse array of
          wildlife exhibits. For arts enthusiasts, the{' '}
          <LinkText href="https://royalmtc.ca/">
            Royal Manitoba Theatre Centre
          </LinkText>{' '}
          and{' '}
          <LinkText href="https://www.wag.ca/">Winnipeg Art Gallery</LinkText>{' '}
          are must-visit destinations. Together, these landmarks make Winnipeg a
          vibrant, culturally rich city.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default Winnipeg
